import React from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Toggle
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import TeamSelectDeprecated from "@/components/messaging/TeamSelect";

interface InstagramConfig {
  id: number;
  username: string;
  displayName: string;
  profilePictureUrl: string;
  pageName: string;
  teamId: number | null;
  autoCreateContact: boolean;
}

interface Props {
  configId: number;
  onClose(): void;
  onSave(): void;
}

export default function InstagramEditModal(props: Props) {
  const api = useApi();
  const [config, setConfig] = React.useState<InstagramConfig | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function load() {
      try {
        const resp = await api.messaging.get(`Instagram/Integrations/${props.configId}`);
        if (resp.ok) setConfig(await resp.json());
      } catch {
        setError("Failed to load configuration.");
      }
    }
    load();
  }, [api.messaging, props.configId]);

  function update(changes: Partial<InstagramConfig>) {
    setConfig((prev) => (prev ? { ...prev, ...changes } : prev));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!config) return;

    setSaving(true);
    setError(null);
    try {
      const resp = await api.messaging.put(`Instagram/Integrations/${props.configId}`, {
        teamId: config.teamId ?? 0,
        autoCreateContact: config.autoCreateContact
      });

      if (resp.ok) {
        props.onSave();
      } else {
        const data = await resp.json().catch(() => null);
        setError(data?.error ?? "Failed to save. Please try again.");
      }
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const title = config
    ? `@${config.username || config.displayName || "Instagram Account"}`
    : "Instagram Account";

  return (
    <Modal show={true}>
      <ModalHeader title={title} onClose={props.onClose} />
      <form onSubmit={handleSave}>
        <ModalBody>
          <div className="p-4 space-y-4">
            {/* Profile summary */}
            {config && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                {config.profilePictureUrl ? (
                  <img
                    src={config.profilePictureUrl}
                    alt={config.username}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <i className="fab fa-instagram text-white" aria-hidden="true" />
                  </div>
                )}
                <div>
                  <div className="font-medium text-slate-800">
                    {config.displayName || config.username}
                  </div>
                  {config.pageName && (
                    <div className="text-xs text-slate-500">via {config.pageName}</div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded">
                {error}
              </div>
            )}

            {/* Team assignment */}
            <div>
              <label htmlFor="ig-team" className="block text-sm font-medium text-slate-700 mb-1">
                Route DMs to team
              </label>
              <TeamSelectDeprecated
                id="ig-team"
                className="input w-full"
                value={config?.teamId ?? undefined}
                onChange={(e) =>
                  update({ teamId: e.currentTarget.value ? parseInt(e.currentTarget.value) : null })
                }
              />
              <p className="text-xs text-slate-400 mt-1">
                Incoming DMs will be routed to this team. Leave blank to route to all agents.
              </p>
            </div>

            {/* Auto-create contact */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-700">Auto-create contact</div>
                <div className="text-xs text-slate-400">
                  Automatically create a contact record for new visitors
                </div>
              </div>
              <Toggle
                toggled={config?.autoCreateContact ?? true}
                changeToggle={() => update({ autoCreateContact: !(config?.autoCreateContact ?? true) })}
              />
            </div>

          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex items-center justify-end gap-3 p-4">
            <SecondaryBlueButton label="Cancel" onClick={props.onClose} />
            <PrimaryBlueButton
              label={saving ? "Saving..." : "Save"}
              type="submit"
              disabled={saving || !config}
            />
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
